import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';

interface FooterLogoSettings {
  footer_logo_url: string;
  footer_show_logo_image: boolean;
  footer_logo_width: string;
  footer_logo_height: string;
}

const defaultFooterLogoSettings: FooterLogoSettings = {
  footer_logo_url: '',
  footer_show_logo_image: false,
  footer_logo_width: '48',
  footer_logo_height: '48'
};

export const useFooterLogoSettings = () => {
  const [footerLogoSettings, setFooterLogoSettings] = useState<FooterLogoSettings>(defaultFooterLogoSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFooterLogoSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('settings')
        .select('*')
        .eq('category', 'footer')
        .in('setting_key', ['footer_logo_url', 'footer_show_logo_image', 'footer_logo_width', 'footer_logo_height'])
        .eq('is_active', true);

      if (fetchError) {
        throw fetchError;
      }

      if (data && data.length > 0) {
        const settings: Partial<FooterLogoSettings> = {};
        
        data.forEach((setting: any) => {
          if (setting.setting_key in defaultFooterLogoSettings) {
            const value = setting.setting_value;
            // Convert string booleans to actual booleans
            if (setting.setting_type === 'boolean') {
              (settings as any)[setting.setting_key] = value === 'true';
            } else {
              (settings as any)[setting.setting_key] = value;
            }
          }
        });

        setFooterLogoSettings({ ...defaultFooterLogoSettings, ...settings });
      } else {
        setFooterLogoSettings(defaultFooterLogoSettings);
      }
    } catch (err) {
      console.error('Error fetching footer logo settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch footer logo settings');
      setFooterLogoSettings(defaultFooterLogoSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateFooterLogoSettings = useCallback(async (newSettings: Partial<FooterLogoSettings>) => {
    try {
      setError(null);

      const updates = Object.entries(newSettings).map(([key, value]) => ({
        setting_key: key,
        setting_value: String(value),
        setting_type: typeof value === 'boolean' ? 'boolean' : 'string',
        category: 'footer',
        description: `Footer ${key.replace(/_/g, ' ')}`,
        is_active: true,
        updated_at: new Date().toISOString()
      }));

      const { error: updateError } = await supabase
        .from('settings')
        .upsert(updates, { onConflict: 'setting_key' });

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setFooterLogoSettings(prev => ({ ...prev, ...newSettings }));
      
      return { success: true };
    } catch (err) {
      console.error('Error updating footer logo settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to update footer logo settings');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to update footer logo settings' };
    }
  }, []);

  useEffect(() => {
    fetchFooterLogoSettings();
  }, [fetchFooterLogoSettings]);

  return {
    footerLogoSettings,
    loading,
    error,
    updateFooterLogoSettings,
    refetch: fetchFooterLogoSettings
  };
};